import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Grid, Container, Alert, Box, Typography } from "@mui/material";
import config from "../../config.js";
import "./stops.css";
import LoadingScreen from "./LoadingScreen";
import VehicleInfoPanel from "./VehicleInfoPanel";
import MapComponent from "./MapComponent";
import destinationSVG from "../../images/destination.svg";
import {
  parseCoordinate,
  hasValidCoordinates,
  processUniqueRoutes,
  extractAllVehicles,
  getDestinationStop,
  vehiclesVisitingStop,
  getClosestPointOnRoute,
  calculateHaversineDistance,
} from "./StopscrennFunctions";
import {
  getRoutesByOrgIdIndependent,
  getVehicleDirection,
  updateVehicleDirection,
} from "../../components/dashboard/dashboardAPI";
import {
  normalizeDeviceData,
  upsertDirectionForVehicle
} from "../../components/dashboard/mapConstants";
import { calculateDistance, calculateBearing, toRad, toDeg } from "../../utils/calculateDistance";
import PoweredBy_Logo from "../../images/poweredBy.svg";
import stopJson from "../../data/stop/stop.json";
import { getIsDemo } from "../../utils/helperFunction";
import { useSettings } from "../../context/SettingsContext";
import StopQrCode from "./StopQrCode";
import { buildStopAnnouncement, tripStatusSignature } from "./stopAnnouncement";
import useStopArrivalAnnouncement from "./useStopArrivalAnnouncement";
import usePublicScreenAd from "../../hooks/usePublicScreenAd";
import PublicAdOverlay from "../../components/ads/PublicAdOverlay";
import PublicAdTicker, { adTickerSpaceSx } from "../../components/ads/PublicAdTicker";
// import { getClosestPointOnRoute } from "./stopscreenFunxtions";

const libraries = ["places", "directions"];
// const getIsDemo() = getIsDemo();

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function StopsScreen() {
  const { stopId } = useParams();
  const { settings, fetchSettings, updateSettings } = useSettings();
  const [isLoading, setIsLoading] = useState(true);
  const [stopsData, setStopsData] = useState([]);
  const [routeData, setRouteData] = useState([]);
  const [directions, setDirections] = useState([]);
  const [error, setError] = useState(null);
  const [highlightedStopId, setHighlightedStopId] = useState(null);
  const [mapCenter, setMapCenter] = useState();
  const mapRef = useRef(null);
  const [vehicleData, setVehicleData] = useState({});
  const [allVehicles, setAllVehicles] = useState([]);
  const [calculatingDistances, setCalculatingDistances] = useState(false);
  const [vehiclesToSource, setVehiclesToSource] = useState([]);
  const [vehiclesToDestination, setVehiclesToDestination] = useState([]);
  const [vehiclesByDirection, setVehiclesByDirection] = useState([]);
  const [vehicleDirections, setVehicleDirections] = useState({});
  const eventSourceRef = useRef(null);
  const isMountedRef = useRef(true);
  const directionsLoadingRef = useRef(new Set());
  const directionsLoadedRef = useRef(new Set());
  const initialDirectionsFetchedRef = useRef(false);
  const lastPositionRef = useRef({});
  const positionHistoryRef = useRef({});
  const [vehicleStatuses, setVehicleStatuses] = useState({});
  const lastDataReceivedRef = useRef({});
  const [vehicleStopStatuses, setVehicleStopStatuses] = useState({});
  const previousStopStatusRef = useRef({});
  const [visitedStops, setVisitedStops] = useState(new Set()); // Added visitedStops state
  const [stopDistances, setStopDistances] = useState({});
  const [stopToStopDistances, setStopToStopDistances] = useState({});
  const [tripStatusByDevice, setTripStatusByDevice] = useState({});
  const [stopAnnouncement, setStopAnnouncement] = useState(null);
  const arrivalCandidatesRef = useRef({});
  const highlightedStopIdRef = useRef(null);

  const MIN_MOVEMENT_THRESHOLD = 5; // 5 meters
  const DEAD_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes in ms
  // const STOPPED_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes in ms
  const [STOPPED_THRESHOLD_MS, setSTOPPED_THRESHOLD_MS] = useState(5 * 60 * 1000); // Default 5 minutes
  useEffect(() => {
    const initializeInactiveTime = async () => {
      try {
        const inactiveTimeMs = await getInactiveTimeFromLocalStorageOrAPI();
        setSTOPPED_THRESHOLD_MS(inactiveTimeMs);
      } catch (error) {
        console.error("Error initializing inactive time:", error);
        setSTOPPED_THRESHOLD_MS(5 * 60 * 1000); // Fallback to 5 minutes
      }
    };
    initializeInactiveTime();
  }, []);

  const STATUS_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes in ms
  const MAX_HISTORY_LENGTH = 5; // Store last 5 positions
  const PASSED_DISTANCE_THRESHOLD = 200;
  const STOP_PROXIMITY_THRESHOLD = 0.2;
  const STOP_LEFT_THRESHOLD = 0.3;
  const STOP_VISIT_TIME = 5000; // 5 seconds threshold to mark a stop as visited

  const navigate = useNavigate();
  const query = useQuery();
  const orgId = query.get("org");
  const InfoSide = query.get("InfoSide");

  useEffect(() => {
    if (!orgId) return;

    fetchSettings(orgId).catch((error) => {
      console.error("Error fetching stop-screen app settings:", error);
    });
  }, [fetchSettings, orgId]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key !== "appSettings" || !event.newValue) return;

      try {
        updateSettings(JSON.parse(event.newValue));
      } catch (error) {
        console.error("Error syncing app settings from another tab:", error);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [updateSettings]);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries,
  });

  const getVehicleStatus = useCallback((deviceId, fallbackStatus) => {
    return vehicleStatuses[deviceId] || fallbackStatus || "UNKNOWN";
  }, [vehicleStatuses]);

  const selectedVehicleStatusFilters = useMemo(() => {
    const configuredStatuses = Array.isArray(settings?.vehiclesStatus)
      ? settings.vehiclesStatus
      : JSON.parse(localStorage.getItem("appSettings") || "{}")?.vehiclesStatus;

    if (!Array.isArray(configuredStatuses) || configuredStatuses.length === 0) {
      return ["active", "inactive"];
    }

    return configuredStatuses.map((status) => String(status).toLowerCase());
  }, [settings?.vehiclesStatus]);

  const showStopQRCode = useMemo(() => {
    if (typeof settings?.showStopQRCode === "boolean") {
      return settings.showStopQRCode;
    }

    try {
      const localSettings = JSON.parse(localStorage.getItem("appSettings") || "{}");
      return Boolean(localSettings.showStopQRCode);
    } catch (error) {
      console.error("Error reading showStopQRCode from localStorage:", error);
      return false;
    }
  }, [settings?.showStopQRCode]);

  const getInactiveTimeFromLocalStorageOrAPI = async () => {
    try {
      // First try to get from localStorage
      const appSettingsString = localStorage.getItem("appSettings");
      if (appSettingsString) {
        const appSettings = JSON.parse(appSettingsString);

        // Get inactiveTime value (assuming it's stored in minutes)
        if (appSettings.inactiveTime && typeof appSettings.inactiveTime === 'number') {
          return appSettings.inactiveTime * 60 * 1000; // Convert minutes to milliseconds
        }
      }

      // If not in localStorage, call API

      // Get organizationId from URL params
      const urlParams = new URLSearchParams(window.location.search);
      const organizationId = urlParams.get('org');

      if (!organizationId) {
        const pathParts = window.location.pathname.split('/');
        const orgFromPath = pathParts[pathParts.length - 1];

        if (!orgFromPath || isNaN(orgFromPath)) {
          console.warn("Organization ID not found in URL params or path, using default inactive time");
          return 5 * 60 * 1000; // Default 5 minutes
        }
        organizationId = orgFromPath;
      }

      // Make API call to get app settings
      const response = await axios.get(
        `${config.baseURL}/appSettings/organization`,
        {
          params: { organizationId }
        }
      );

      if (response.data && response.data.inactiveTime) {
        const inactiveTimeMs = response.data.inactiveTime * 60 * 1000; // Convert minutes to milliseconds

        // Store in localStorage for future use
        const existingSettings = JSON.parse(localStorage.getItem("appSettings") || "{}");
        existingSettings.inactiveTime = response.data.inactiveTime;
        localStorage.setItem("appSettings", JSON.stringify(existingSettings));

        return inactiveTimeMs;
      }

    } catch (error) {
      console.error("Error getting inactive time from localStorage or API:", error);
    }

    // Return default 5 minutes in ms if both localStorage and API fail
    return 5 * 60 * 1000;
  };

  const fetchVehicleDirection = useCallback(async (deviceId) => {
    if (directionsLoadingRef.current.has(deviceId)) {
      return vehicleDirections[deviceId] || "unknown";
    }
    try {
      directionsLoadingRef.current.add(deviceId);
      const direction = await getVehicleDirection(deviceId);
      const finalDirection = direction || "unknown";
      const existingData = JSON.parse(localStorage.getItem(deviceId) || "{}");
      const updatedData = {
        ...existingData,
        direction: finalDirection,
        timestamp: Date.now(),
        deviceId: deviceId,
        version: existingData.version || "2.0",
      };
      localStorage.setItem(deviceId, JSON.stringify(updatedData));
      setVehicleDirections((prev) => ({
        ...prev,
        [deviceId]: finalDirection,
      }));
      directionsLoadedRef.current.add(deviceId);
      directionsLoadingRef.current.delete(deviceId);
      return finalDirection;
    } catch (error) {
      console.error(`Error fetching direction from Redis for ${deviceId}:`, error);
      try {
        const storedData = JSON.parse(localStorage.getItem(deviceId) || "{}");
        const fallbackDirection = storedData.direction || "unknown";
        // console.log(`Using localStorage fallback for ${deviceId}: ${fallbackDirection}`);
        setVehicleDirections((prev) => ({
          ...prev,
          [deviceId]: fallbackDirection,
        }));
        directionsLoadingRef.current.delete(deviceId);
        return fallbackDirection;
      } catch (storageError) {
        console.error(`Error reading from localStorage for ${deviceId}:`, storageError);
        directionsLoadingRef.current.delete(deviceId);
        return "unknown";
      }
    }
  }, []);

  const fetchAllDirections = useCallback(async (vehicles) => {
    if (!vehicles?.length) return {};
    // console.log("Fetching all directions from Redis...");
    try {
      initialDirectionsFetchedRef.current = false;
      const batchSize = 3;
      const directionMap = {};
      for (let i = 0; i < vehicles.length; i += batchSize) {
        const batch = vehicles.slice(i, i + batchSize);
        const batchPromises = batch.map(async (vehicle) => {
          try {
            const direction = await fetchVehicleDirection(vehicle.device_id);
            return { deviceId: vehicle.device_id, direction };
          } catch (error) {
            console.error(`Failed to fetch direction for ${vehicle.device_id}:`, error);
            return { deviceId: vehicle.device_id, direction: "unknown" };
          }
        });
        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach(({ deviceId, direction }) => {
          directionMap[deviceId] = direction;
        });
        if (i + batchSize < vehicles.length) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }
      setVehicleDirections(directionMap);
      initialDirectionsFetchedRef.current = true;
      return directionMap;
    } catch (error) {
      console.error("Error fetching all vehicle directions from Redis:", error);
      try {
        const fallbackDirections = {};
        vehicles.forEach((vehicle) => {
          const storedData = JSON.parse(localStorage.getItem(vehicle.device_id) || "{}");
          fallbackDirections[vehicle.device_id] = storedData.direction || "unknown";
        });
        // console.log("Using localStorage fallback for all directions");
        setVehicleDirections(fallbackDirections);
        return fallbackDirections;
      } catch (storageError) {
        console.error("Error reading from localStorage:", storageError);
        return {};
      }
    }
  }, [fetchVehicleDirection]);

  const fetchInitialLocations = useCallback(async () => {
    if (!allVehicles.length) return;
    try {
      const directions = await fetchAllDirections(allVehicles);
      const promises = allVehicles.map(async ({ device_id }) => {
        const response = await fetch(`${config.baseURL}/location/latest/${device_id}`);
        const data = await response.json();
        if (data && data.latitude != null && data.longitude != null) {
          return normalizeDeviceData(data);
        } else {
          console.warn(`No valid location data for device ${device_id}`, data);
          return null;
        }
      });
      const locations = await Promise.all(promises);
      const validLocations = locations.filter((loc) => loc !== null);
      const locationObject = {};
      for (const loc of validLocations) {
        const { deviceId, latitude, longitude, speed } = loc;
        locationObject[deviceId] = {
          position: {
            lat: parseCoordinate(latitude),
            lng: parseCoordinate(longitude),
          },
          speed: speed || 0,
        };
        positionHistoryRef.current[deviceId] = [
          {
            lat: parseCoordinate(latitude),
            lng: parseCoordinate(longitude),
            timestamp: Date.now(),
          },
        ];
      }
      if (!isMountedRef.current) return;
      setVehicleData(locationObject);
    } catch (error) {
      console.error("Error fetching initial vehicle locations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [allVehicles, fetchAllDirections]);

  const updateLocalStorageWithPosition = useCallback((deviceId, position) => {
    try {
      const existingData = JSON.parse(localStorage.getItem(deviceId) || "{}");
      const currentTime = Date.now();
      if (!existingData.positions) {
        existingData.positions = [];
      }
      const newPosition = {
        latitude: position.latitude.toString(),
        longitude: position.longitude.toString(),
        timestamp: currentTime,
      };
      existingData.positions.push(newPosition);
      if (existingData.positions.length > 10) {
        existingData.positions = existingData.positions.slice(-10);
      }
      existingData.lastMovedPosition = {
        latitude: position.latitude.toString(),
        longitude: position.longitude.toString(),
      };
      existingData.timestamp = currentTime;
      existingData.deviceId = deviceId;
      existingData.version = existingData.version || "2.0";
      if (!existingData.direction) {
        existingData.direction = "unknown";
      }
      localStorage.setItem(deviceId, JSON.stringify(existingData));
    } catch (error) {
      console.error(`Error updating localStorage for ${deviceId}:`, error);
    }
  }, []);
  const fetchDemoLocations = async () => {
    if (!allVehicles.length) return;

    try {
      let locations;
      if (getIsDemo()) {
        locations = stopJson?.Stop?.stopsScreens?.["/api/location/latest"]?.map((data) => normalizeDeviceData(data));;
      } else {
        const promises = allVehicles.map(async ({ device_id }) => {
          const response = await fetch(
            `${config.baseURL}/location/latest/${device_id}`
          );
          const data = await response.json();

          if (data && data.latitude != null && data.longitude != null) {
            return normalizeDeviceData(data);
          } else {
            console.warn(
              `No valid location data for device ${device_id}`,
              data
            );
            return null;
          }
        });

        locations = await Promise.all(promises);
      }

      const validLocations = locations.filter((loc) => loc !== null);

      const locationObject = {};
      for (const loc of validLocations) {
        const { deviceId, latitude, longitude, speed } = loc;
        locationObject[deviceId] = {
          position: {
            lat: parseCoordinate(latitude),
            lng: parseCoordinate(longitude),
          },
          speed: speed || 0,
        };
      }

      if (!isMountedRef.current) return;
      setVehicleData(locationObject);
    } catch (error) {
      console.error("Error fetching initial vehicle locations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    if (allVehicles.length > 0) {
      if (getIsDemo()) {
        fetchDemoLocations();
      } else {
        fetchInitialLocations();
      }
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [allVehicles]);

  useEffect(() => {
    if (allVehicles.length > 0 && !initialDirectionsFetchedRef.current) {
      directionsLoadedRef.current.clear();
      directionsLoadingRef.current.clear();
      fetchAllDirections(allVehicles);
    }
  }, [allVehicles, fetchAllDirections]);

  const onDirectionChange = useCallback(async (deviceId, direction) => {
    try {
      // console.log(`Updating direction for ${deviceId} to ${direction} in Redis`);
      await updateVehicleDirection(deviceId, direction);
      setVehicleDirections((prev) => ({
        ...prev,
        [deviceId]: direction,
      }));
      const existingData = JSON.parse(localStorage.getItem(deviceId) || "{}");
      existingData.direction = direction;
      existingData.timestamp = Date.now();
      existingData.deviceId = deviceId;
      existingData.version = existingData.version || "2.0";
      localStorage.setItem(deviceId, JSON.stringify(existingData));
      positionHistoryRef.current[deviceId] = [];
      // console.log(`Successfully updated direction for ${deviceId} in Redis and localStorage`);
    } catch (error) {
      console.error(`Failed to update direction for ${deviceId} in Redis:`, error);
      const existingData = JSON.parse(localStorage.getItem(deviceId) || "{}");
      existingData.direction = direction;
      existingData.timestamp = Date.now();
      existingData.deviceId = deviceId;
      existingData.version = existingData.version || "2.0";
      localStorage.setItem(deviceId, JSON.stringify(existingData));
      positionHistoryRef.current[deviceId] = [];
    }
  }, []);

const handleSSEMessage = useCallback(
  async (raw) => {
    if (raw.type === "connected") return;
    const normalized = normalizeDeviceData(raw);
    const { deviceId } = normalized;

    if (raw.tripStatusEvent && deviceId) {
      const event = raw.tripStatusEvent;
      setTripStatusByDevice((prev) => {
        if (tripStatusSignature(prev[deviceId]) === tripStatusSignature(event)) {
          return prev;
        }
        return { ...prev, [deviceId]: event };
      });
    }

    if (!(normalized.latitude && normalized.longitude && deviceId)) return;
    const newPosition = {
      lat: parseCoordinate(normalized.latitude),
      lng: parseCoordinate(normalized.longitude),
    };
    const currentTime = Date.now();

    lastDataReceivedRef.current[deviceId] = currentTime;

    setVehicleStatuses((prev) => ({
      ...prev,
      [deviceId]: "ACTIVE",
    }));

    const lastPos = lastPositionRef.current[deviceId];
    if (lastPos) {
      const distance = calculateDistance(lastPos.position, newPosition) || 0;
      if (distance < MIN_MOVEMENT_THRESHOLD) {
        const timeDiff = currentTime - lastPos.timestamp;
        if (timeDiff > STOPPED_THRESHOLD_MS) {
          return;
        }
      }
    }

    lastPositionRef.current[deviceId] = {
      position: newPosition,
      timestamp: currentTime,
    };

    if (!positionHistoryRef.current[deviceId]) {
      positionHistoryRef.current[deviceId] = [];
    }
    positionHistoryRef.current[deviceId].push({
      lat: newPosition.lat,
      lng: newPosition.lng,
      timestamp: currentTime,
    });
    if (positionHistoryRef.current[deviceId].length > MAX_HISTORY_LENGTH) {
      positionHistoryRef.current[deviceId] =
        positionHistoryRef.current[deviceId].slice(-MAX_HISTORY_LENGTH);
    }

    // === HEADING CALCULATION ===
    let heading = 0;
    const history = positionHistoryRef.current[deviceId];

    if (history.length >= 2) {
      const prevPos = history[history.length - 2];
      const currPos = history[history.length - 1];

      // Only calculate heading if movement happened
      if (prevPos.lat !== currPos.lat || prevPos.lng !== currPos.lng) {
        heading = calculateBearing(
          { latitude: prevPos.lat, longitude: prevPos.lng },
          { latitude: currPos.lat, longitude: currPos.lng }
        );
      } else {
        // Keep previous heading from state or localStorage
        heading =
          vehicleData[deviceId]?.heading ||
          JSON.parse(localStorage.getItem(deviceId) || "{}")?.lastHeading ||
          0;
      }
    } else {
      // If no history, reuse previous heading if exists
      heading =
        vehicleData[deviceId]?.heading ||
        JSON.parse(localStorage.getItem(deviceId) || "{}")?.lastHeading ||
        0;
    }

    // === UPDATE VEHICLE DATA & PERSIST ===
    setVehicleData((prev) => {
      const updatedVehicle = {
        ...prev[deviceId],
        position: newPosition,
        speed: normalized.speed || 0,
        status: "ACTIVE",
        heading,
      };

      // persist last heading + position in localStorage
      const persisted = JSON.parse(localStorage.getItem(deviceId) || "{}");
      persisted.lastHeading = updatedVehicle.heading;
      persisted.lastPosition = updatedVehicle.position;
      localStorage.setItem(deviceId, JSON.stringify(persisted));

      return {
        ...prev,
        [deviceId]: updatedVehicle,
      };
    });

    updateLocalStorageWithPosition(deviceId, {
      latitude: normalized.latitude,
      longitude: normalized.longitude,
    });

    // === FETCH/UPSERT DIRECTION ===
    const currentDirection = vehicleDirections[deviceId];
    const lastFetch = directionsLoadedRef.current.has(deviceId);

    if (!currentDirection || currentDirection === "unknown" || !lastFetch) {
      const existingData = JSON.parse(localStorage.getItem(deviceId) || "{}");
      const lastRedisCheck = existingData.lastRedisCheck || 0;
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (now - lastRedisCheck > fiveMinutes) {
        await fetchVehicleDirection(deviceId);
        const updatedData = JSON.parse(localStorage.getItem(deviceId) || "{}");
        updatedData.lastRedisCheck = now;
        localStorage.setItem(deviceId, JSON.stringify(updatedData));
      } else {
        await upsertDirectionForVehicle({
          deviceId,
          currentPosition: {
            latitude: normalized.latitude,
            longitude: normalized.longitude,
          },
          stops: stopsData,
          routes: routeData,
          vehicles: allVehicles,
          setVehicleDirections: (updateFn) => {
            setVehicleDirections((prev) => {
              const newDirections =
                typeof updateFn === "function" ? updateFn(prev) : updateFn;
              const updatedDirection = newDirections[deviceId];

              if (prev[deviceId] && prev[deviceId] !== "unknown") {
                return prev;
              }
              if (updatedDirection) {
                const existingData =
                  JSON.parse(localStorage.getItem(deviceId) || "{}");
                existingData.direction = updatedDirection;
                existingData.timestamp = Date.now();
                localStorage.setItem(deviceId, JSON.stringify(existingData));
              }
              return newDirections;
            });
          },
          onDirectionChange,
        });
      }
    }

    // === VISITED STOPS ===
    const stopStatus = vehicleStopStatuses[deviceId];
    if (stopStatus?.status === "at") {
      const stop = stopsData.find((s) => s.name === stopStatus.stopName);
      if (stop && !visitedStops.has(stop.id)) {
        const startTime =
          lastPositionRef.current[deviceId]?.timestamp || currentTime;
        if (currentTime - startTime >= STOP_VISIT_TIME) {
          setVisitedStops((prev) => new Set(prev).add(stop.id));
        }
      }
    }
  },
  [
    stopsData,
    routeData,
    allVehicles,
    onDirectionChange,
    vehicleDirections,
    fetchVehicleDirection,
    updateLocalStorageWithPosition,
    MIN_MOVEMENT_THRESHOLD,
    STOPPED_THRESHOLD_MS,
    MAX_HISTORY_LENGTH,
    vehicleStopStatuses,
    visitedStops,
  ]
);


  useEffect(() => {
    const fetchRouteData = async () => {
      try {
        const routeData = await getRoutesByOrgIdIndependent(orgId);
        if (routeData.length > 0 && routeData[0].routeCoordinates) {
          setDirections(routeData[0].routeCoordinates);
        }
      } catch (error) {
        console.error("Error fetching route data:", error);
      }
    };
    fetchRouteData();
  }, []);

  useEffect(() => {
    if (!allVehicles.length || getIsDemo()) return;
    const query = allVehicles.map((v) => `vehicleId=${v.device_id}`).join("&");
    const src = new EventSource(`${config.baseURL}/redis/stream?${query}`);
    src.onmessage = (e) => {
      try {
        handleSSEMessage(JSON.parse(e.data));
      } catch (error) {
        console.error("Error handling SSE message:", error);
      }
    };
    eventSourceRef.current = src;
    return () => src.close();
  }, [allVehicles]);

  const isVehicleApproaching = useCallback((deviceId, stop, direction, route) => {
    const livePos = vehicleData[deviceId]?.position;
    if (!livePos || !route?.coordinates?.length || !direction || direction === "unknown") return false;
    const stopPos = {
      latitude: parseFloat(stop.latitude),
      longitude: parseFloat(stop.longitude),
    };
    const vehicleClosest = getClosestPointOnRoute(
      { latitude: livePos.lat, longitude: livePos.lng },
      route.coordinates
    );
    const stopRouteIndex = route.coordinates.findIndex(
      (coord) =>
        (coord.stopId && coord.stopId === stop.id) ||
        (Math.abs(parseFloat(coord.lat || coord.latitude) - stopPos.latitude) < 0.0002 &&
          Math.abs(parseFloat(coord.lng || coord.longitude) - stopPos.longitude) < 0.0002)
    );
    if (vehicleClosest.index === -1 || stopRouteIndex === -1) {
      const history = positionHistoryRef.current[deviceId] || [];
      if (history.length < 2) return true;
      const distances = history
        .map((pos) => calculateDistance(pos, stopPos))
        .filter((d) => d !== null);
      if (distances.length < 2) return true;
      return distances[distances.length - 1] <= distances[0] + MIN_MOVEMENT_THRESHOLD;
    }
    if (direction === "source_to_dest") {
      return vehicleClosest.index <= stopRouteIndex;
    } else if (direction === "dest_to_source") {
      return vehicleClosest.index >= stopRouteIndex;
    }
    return false;
  }, [vehicleData, positionHistoryRef, MIN_MOVEMENT_THRESHOLD]);

  const isVehiclePassed = useCallback(
    (vehicle, stop) => {
      const { device_id: deviceId, position, direction, routeId } = vehicle;
      if (!position || !direction || direction === "unknown") return false;
      const stopPos = {
        latitude: parseFloat(stop.latitude),
        longitude: parseFloat(stop.longitude),
      };
      const route = routeData.find((r) => r.id === routeId);
      if (!route?.coordinates?.length) return false;
      const vehicleClosest = getClosestPointOnRoute(
        { latitude: position.lat, longitude: position.lng },
        route.coordinates
      );
      const stopRouteIndex = route.coordinates.findIndex(
        (coord) =>
          (coord.stopId && coord.stopId === stop.id) ||
          (Math.abs(parseFloat(coord.lat || coord.latitude) - stopPos.latitude) < 0.0002 &&
            Math.abs(parseFloat(coord.lng || coord.longitude) - stopPos.longitude) < 0.0002)
      );
      if (vehicleClosest.index === -1 || stopRouteIndex === -1) {
        const dist = calculateDistance(position, stopPos);
        return dist !== null && dist > PASSED_DISTANCE_THRESHOLD && !isVehicleApproaching(deviceId, stop, direction, route);
      }
      const distanceToStop = calculateDistance(position, stopPos);
      const isVeryClose = distanceToStop !== null && distanceToStop < PASSED_DISTANCE_THRESHOLD;
      if (direction === "source_to_dest") {
        return vehicleClosest.index > stopRouteIndex + 2 && !isVeryClose;
      } else if (direction === "dest_to_source") {
        return vehicleClosest.index < stopRouteIndex - 2 && !isVeryClose;
      }
      return false;
    },
    [routeData, isVehicleApproaching, PASSED_DISTANCE_THRESHOLD]
  );

  const isVehicleStopped = useCallback((deviceId, currentPosition, status) => {
    const currentStatus = getVehicleStatus(deviceId, status);
    if (status === "INACTIVE" || status === "DEAD") {
      return true;
    }
    const lastPos = lastPositionRef.current[deviceId];
    const now = Date.now();
    if (!lastPos) {
      lastPositionRef.current[deviceId] = {
        position: currentPosition,
        timestamp: now,
      };
      return false;
    }
    const lastDist = calculateDistance(lastPos.position, currentPosition);
    const timeDiff = now - lastPos.timestamp;
    if (lastDist === null || lastDist > MIN_MOVEMENT_THRESHOLD || timeDiff < STOPPED_THRESHOLD_MS) {
      lastPositionRef.current[deviceId] = {
        position: currentPosition,
        timestamp: now,
      };
      return false;
    }
    return true;
  }, [getVehicleStatus]);

  const getDerivedVehicleVisibilityStatus = useCallback((vehicle) => {
    const deviceId = vehicle?.device_id;
    const livePosition = vehicleData[deviceId]?.position;
    const currentStatus = String(
      getVehicleStatus(deviceId, vehicle?.status),
    ).toUpperCase();

    if (currentStatus === "DEAD") {
      return "dead";
    }

    if (currentStatus === "INACTIVE") {
      return "inactive";
    }

    if (currentStatus === "UNKNOWN" || !currentStatus) {
      return "inactive";
    }

    const lastPos = lastPositionRef.current[deviceId];
    if (livePosition && lastPos?.position) {
      const lastDist = calculateDistance(lastPos.position, livePosition);
      const timeDiff = Date.now() - lastPos.timestamp;

      if (
        lastDist !== null &&
        lastDist <= MIN_MOVEMENT_THRESHOLD &&
        timeDiff >= STOPPED_THRESHOLD_MS
      ) {
        return "inactive";
      }
    }

    return "active";
  }, [
    MIN_MOVEMENT_THRESHOLD,
    STOPPED_THRESHOLD_MS,
    getVehicleStatus,
    vehicleData,
  ]);

  const visibleVehicles = useMemo(() => {
    if (!allVehicles.length) return [];

    return allVehicles.filter((vehicle) =>
      selectedVehicleStatusFilters.includes(
        getDerivedVehicleVisibilityStatus(vehicle),
      ),
    );
  }, [
    allVehicles,
    getDerivedVehicleVisibilityStatus,
    selectedVehicleStatusFilters,
  ]);

  const determineStopStatus = useCallback((vehicle, stops) => {
    if (!vehicle.position || !stops || stops.length === 0) {
      return { status: null, stopName: null };
    }
    const vehiclePos = vehicle.position;
    const previousStatus = previousStopStatusRef.current[vehicle.device_id] || { status: null, stopName: null };
    let closestStop = null;
    let minDistance = Infinity;
    stops.forEach(stop => {
      const distance = calculateDistance(vehiclePos, {
        latitude: stop.latitude,
        longitude: stop.longitude
      });
      if (distance !== null && distance < minDistance) {
        minDistance = distance;
        closestStop = stop;
      }
    });
    if (!closestStop) {
      return { status: null, stopName: null };
    }
    const stopName = closestStop.name || closestStop.stopName || 'Unknown Stop';
    if (minDistance <= STOP_PROXIMITY_THRESHOLD) {
      return { status: 'at', stopName };
    }
    if (
      minDistance <= STOP_LEFT_THRESHOLD &&
      previousStatus.stopName === stopName &&
      previousStatus.status === 'at'
    ) {
      return { status: 'left', stopName };
    }
    if (previousStatus.status === 'left' && previousStatus.stopName === stopName) {
      if (minDistance <= STOP_LEFT_THRESHOLD * 1.5) {
        return { status: 'left', stopName };
      }
    }
    return { status: null, stopName: null };
  }, []);

  useEffect(() => {
    if (!allVehicles.length || !stopsData.length) return;
    const newStopStatuses = {};
    allVehicles.forEach(vehicle => {
      const vehiclePosition = vehicleData[vehicle.device_id]?.position;
      if (vehiclePosition) {
        const vehicleWithPosition = { ...vehicle, position: vehiclePosition };
        const stopStatus = determineStopStatus(vehicleWithPosition, stopsData);
        newStopStatuses[vehicle.device_id] = stopStatus;
        previousStopStatusRef.current[vehicle.device_id] = stopStatus;
      }
    });
    setVehicleStopStatuses(newStopStatuses);
  }, [allVehicles, stopsData, vehicleData, determineStopStatus]);

  const updateVehiclesPanels = useCallback(
    (stop) => {
      const serving = vehiclesVisitingStop(stop, visibleVehicles, routeData);
      const enriched = serving.map((v) => {
        const dir = vehicleDirections[v.device_id] || "unknown";
        const route = routeData.find((r) => r.id === v.routeId);
        const destLabel = getDestinationStop(route, dir, stopsData);
        const livePos = vehicleData[v.device_id]?.position;
        const stopPos = {
          latitude: parseFloat(stop.latitude),
          longitude: parseFloat(stop.longitude),
        };
        const dist = livePos ? calculateDistance(livePos, stopPos) : null;
        const currentStatus = getVehicleStatus(v.device_id, v.status);
        const isStopped = isVehicleStopped(v.device_id, livePos || { lat: 0, lng: 0 }, currentStatus);
        const hasPassed = isVehiclePassed(v, stop);
        const isApproaching = isVehicleApproaching(v.device_id, stop, dir, route);
        const nextStop = route?.routeCoordinates
          ? route.routeCoordinates
            .map((coord, index) => ({
              ...coord,
              routeIndex: index,
              distance: calculateDistance(livePos, {
                latitude: parseFloat(coord.latitude),
                longitude: parseFloat(coord.longitude),
              }),
            }))
            .filter((coord) => coord.distance !== null && !visitedStops.has(coord.stopId))
            .sort((a, b) => (dir === "source_to_dest" ? a.routeIndex - b.routeIndex : b.routeIndex - a.routeIndex))[0]
          : null;

        return {
          ...v,
          direction: dir,
          destinationLabel: destLabel,
          distanceToStop: dist,
          speed: vehicleData[v.device_id]?.speed || 0,
          position: livePos || null,
          stopPosition: stopPos,
          status: currentStatus,
          route: route,
          isStopped: isStopped,
          hasPassed: hasPassed,
          isApproaching: isApproaching,
          nextStop: nextStop,
          tripStatus: tripStatusByDevice[v.device_id] || null,
        };
      });
      const sourceToDestSorted = [...enriched].sort((a, b) => {
        if (a.direction === "source_to_dest" && b.direction !== "source_to_dest") return -1;
        if (a.direction !== "source_to_dest" && b.direction === "source_to_dest") return 1;
        return 0;
      });
      const destToSourceSorted = [...enriched].sort((a, b) => {
        if (a.direction === "dest_to_source" && b.direction !== "dest_to_source") return -1;
        if (a.direction !== "dest_to_source" && b.direction === "dest_to_source") return 1;
        return 0;
      });
      if (isMountedRef.current) {
        setVehiclesByDirection(enriched);
        setVehiclesToSource(sourceToDestSorted);
        setVehiclesToDestination(destToSourceSorted);
      }
    },
    [
      visibleVehicles,
      routeData,
      stopsData,
      vehicleDirections,
      vehicleData,
      isVehiclePassed,
      isVehicleStopped,
      isVehicleApproaching,
      getVehicleStatus,
      visitedStops, // Added dependency
      tripStatusByDevice,
    ]
  );

  useEffect(() => {
    if (!highlightedStopId || !stopsData.length) {
      setVehiclesByDirection([]);
      setVehiclesToSource([]);
      setVehiclesToDestination([]);
      return;
    }

    if (!visibleVehicles.length) {
      setVehiclesByDirection([]);
      setVehiclesToSource([]);
      setVehiclesToDestination([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      const stop = stopsData.find((s) => s.id.toString() === highlightedStopId.toString());
      if (stop) updateVehiclesPanels(stop);
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [
    highlightedStopId,
    stopsData,
    visibleVehicles,
    vehicleDirections,
    vehicleData,
    updateVehiclesPanels,
  ]);

  useEffect(() => {
    if (performance.navigation.type === performance.navigation.TYPE_RELOAD) {
      // console.log("Page refreshed - clearing direction cache");
      localStorage.removeItem("vehicleDirections");
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("lastRedisCheck_")) {
          localStorage.removeItem(key);
        }
      });
    }
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        let stops;
        if (!getIsDemo()) {
          const stopsRes = await axios.get(
            `${config.baseURL}/stop/organization/independent/${orgId}`,
            { withCredentials: true }
          );
          stops = stopsRes.data
            .map((s) => ({
              ...s,
              latitude: parseCoordinate(s.latitude),
              longitude: parseCoordinate(s.longitude),
            }))
            .filter(hasValidCoordinates);
        }

        let stopsWithRoutes;

        if (getIsDemo()) {
          const coordinates = stopJson?.Stop?.stop?.["api/route/coordinates"];
          const allStop = stopJson?.Stop?.stop?.["stop/organization"];
          stopsWithRoutes = allStop?.map((s) => ({
            ...s,
            // routes: coordinates?.filter(
            //   (cord) => cord?.lat === s.latitude && cord?.lon === s.longitude
            // ),
            routes:coordinates
          }));
        } else {
          stopsWithRoutes = await Promise.all(
            stops.map(async (s) => ({
              ...s,
              routes: await axios
                .get(`${config.baseURL}/route/coordinates/`, {
                  params: {
                    longitude: s.longitude,
                    latitude: s.latitude,
                    organizationId: s.organizationId,
                  },
                })
                .then((r) => r.data),
            }))
          );
        }
        setStopsData(stopsWithRoutes);
        const uniqRoutes = processUniqueRoutes(stopsWithRoutes);
        setRouteData(uniqRoutes);
        setAllVehicles(extractAllVehicles(stopsWithRoutes));
        if (stopId) {
          setHighlightedStopId(parseInt(stopId));
          const targetStop = stopsWithRoutes.find((x) => x.id.toString() === stopId);
          if (targetStop)
            setMapCenter({
              lat: targetStop.latitude,
              lng: targetStop.longitude,
            });
        } else if (stopsWithRoutes.length) {
          setMapCenter({
            lat: stopsWithRoutes[0].latitude,
            lng: stopsWithRoutes[0].longitude,
          });
        }
      } catch (err) {
        if (err.response?.status === 401) navigate("/");
        console.error("Failed to load data:", err);
      }
      setIsLoading(false);
    })();
  }, [navigate, stopId, orgId]);

  const filteredStops = useMemo(() => {
    if (stopId) {
      const target = stopsData.find((s) => s.id.toString() === stopId);
      if (target?.routes?.length) {
        const ids = target.routes.map((r) => r.id.toString());
        return stopsData.filter(
          (s) =>
            hasValidCoordinates(s) &&
            s.routes?.some((r) => ids.includes(r.id.toString()))
        );
      }
    }
    return stopsData.filter(hasValidCoordinates);
  }, [stopsData, stopId]);

  const getRoutesForStop = (stop, routeData) => {
    if (!stop) return [];
    return routeData.filter((route) =>
      route.routeCoordinates?.some(
        (coord) =>
          (coord.stopId && coord.stopId === stop.id) ||
          (Math.abs(parseFloat(coord.lat || coord.latitude) - parseFloat(stop.latitude)) < 0.0002 &&
            Math.abs(parseFloat(coord.lng || coord.longitude) - parseFloat(stop.longitude)) < 0.0002)
      )
    );
  };

  const selectedStop = highlightedStopId
    ? stopsData.find((s) => s.id.toString() === highlightedStopId.toString())
    : null;

  const routesForStop = useMemo(
    () => getRoutesForStop(selectedStop, routeData),
    [selectedStop, routeData]
  );

  const getSourceStopName = (route) => {
    if (!route?.routeCoordinates?.length) return "Source";
    const coord = route.routeCoordinates[0];
    const stop = stopsData.find(
      (s) =>
        s.id === coord.stopId ||
        (Math.abs(parseFloat(s.latitude) - parseFloat(coord.lat || coord.latitude)) < 0.0002 &&
          Math.abs(parseFloat(s.longitude) - parseFloat(coord.lng || coord.longitude)) < 0.0002)
    );
    return stop?.name || coord.stopName || coord.name || coord.stopId || "Source";
  };

  useEffect(() => {
    if (!allVehicles.length) return;
    const updateDirections = async () => {
      // console.log("Updating directions for all vehicles...");
      try {
        const directionMap = {};
        for (let i = 0; i < allVehicles.length; i += 3) {
          const batch = allVehicles.slice(i, i + 3);
          const batchPromises = batch.map(async (vehicle) => {
            try {
              const direction = await getVehicleDirection(vehicle.device_id);
              return { deviceId: vehicle.device_id, direction };
            } catch (error) {
              console.error(`Failed to fetch direction for ${vehicle.device_id}:`, error);
              return {
                deviceId: vehicle.device_id,
                direction: vehicleDirections[vehicle.device_id] || "unknown",
              };
            }
          });
          const batchResults = await Promise.all(batchPromises);
          batchResults.forEach(({ deviceId, direction }) => {
            directionMap[deviceId] = direction;
            const existingData = JSON.parse(localStorage.getItem(deviceId) || "{}");
            existingData.direction = direction;
            existingData.timestamp = Date.now();
            existingData.lastRedisCheck = Date.now();
            localStorage.setItem(deviceId, JSON.stringify(existingData));
          });
          if (i + 3 < allVehicles.length) {
            await new Promise((resolve) => setTimeout(resolve, 200));
          }
        }
        setVehicleDirections((prev) => {
          const hasChanges = Object.entries(directionMap).some(
            ([deviceId, direction]) => prev[deviceId] !== direction
          );
          return hasChanges ? { ...prev, ...directionMap } : prev;
        });
      } catch (error) {
        console.error("Error updating directions:", error);
      }
    };
    updateDirections();
    const intervalId = setInterval(updateDirections, 60000);
    return () => {
      clearInterval(intervalId);
    };
  }, [allVehicles, getVehicleDirection]);

  useEffect(() => {
    if (!allVehicles.length) return;
    const statusCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeoutThreshold = 5 * 60 * 1000;
      setVehicleStatuses((prev) => {
        const newStatuses = { ...prev };
        allVehicles.forEach((vehicle) => {
          const deviceId = vehicle.device_id;
          const lastReceived = lastDataReceivedRef.current[deviceId];
          if (!lastReceived || now - lastReceived > timeoutThreshold) {
            newStatuses[deviceId] = "DEAD";
          }
        });
        return newStatuses;
      });
    }, 3 * 60 * 1000);
    return () => clearInterval(statusCheckInterval);
  }, [allVehicles]);

  const highlightedStopName = useMemo(() => {
    if (!highlightedStopId || !stopsData.length) return null;
    const stop = stopsData.find(s => s.id.toString() === highlightedStopId.toString());
    return stop?.name || null;
  }, [highlightedStopId, stopsData]);


  // Helper function to find the nearest route coordinate index for a given point
  const findNearestRouteCoordinateIndex = (point, routeCoordinates) => {
    if (!hasValidCoordinates(point) || !routeCoordinates?.length) {
      return -1;
    }

    const pointLat = parseFloat(point.latitude || point.lat);
    const pointLng = parseFloat(point.longitude || point.lng);

    let minDistance = Infinity;
    let nearestIndex = -1;

    routeCoordinates.forEach((coord, index) => {
      const coordLat = parseFloat(coord.latitude || coord.lat);
      const coordLng = parseFloat(coord.longitude || coord.lng);

      if (!isNaN(coordLat) && !isNaN(coordLng)) {
        const distance = calculateHaversineDistance(pointLat, pointLng, coordLat, coordLng);
        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = index;
        }
      }
    });

    return nearestIndex;
  };

  const calculateRouteSegmentDistance = (routeCoordinates, startIndex, endIndex) => {
    if (!routeCoordinates?.length || startIndex < 0 || endIndex < 0) {
      return null;
    }

    let totalDistance = 0;
    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);

    for (let i = start; i < end; i++) {
      const currentCoord = routeCoordinates[i];
      const nextCoord = routeCoordinates[i + 1];

      if (!currentCoord || !nextCoord) {
        console.warn(`Missing coordinates at index ${i}`);
        continue;
      }

      const currentLat = parseFloat(currentCoord.latitude || currentCoord.lat);
      const currentLng = parseFloat(currentCoord.longitude || currentCoord.lng);
      const nextLat = parseFloat(nextCoord.latitude || nextCoord.lat);
      const nextLng = parseFloat(nextCoord.longitude || nextCoord.lng);

      if (!isNaN(currentLat) && !isNaN(currentLng) && !isNaN(nextLat) && !isNaN(nextLng)) {
        const segmentDistance = calculateHaversineDistance(currentLat, currentLng, nextLat, nextLng);
        totalDistance += segmentDistance;
      }
    }

    return totalDistance;
  };

  const calculateRouteBasedDistance = (stop, targetPoint, route, isToDestination = true) => {
    if (!route?.routeCoordinates?.length || !hasValidCoordinates(stop)) {
      console.warn(`Invalid inputs for distance calculation:`, {
        stopId: stop?.id,
        routeId: route?.id,
        hasRouteCoords: !!route?.routeCoordinates?.length
      });
      return null;
    }

    const routeCoordinates = route.routeCoordinates.sort((a, b) => (a.order || 0) - (b.order || 0));

    // Find the stop's position in the route
    const stopIndex = findNearestRouteCoordinateIndex(stop, routeCoordinates);

    if (stopIndex === -1) {
      console.warn(`Could not find route index for stop ${stop.id}`);
      return null;
    }

    let cumulativeDistance = 0;

    if (isToDestination) {
      // Calculate step-by-step TO DESTINATION (forward from stop to end)
      // S3 → S4 → S5 (if S3 is at index 2, destination is at index 4)
      for (let i = stopIndex; i < routeCoordinates.length - 1; i++) {
        const currentCoord = routeCoordinates[i];
        const nextCoord = routeCoordinates[i + 1];

        if (currentCoord && nextCoord) {
          const currentLat = parseFloat(currentCoord.latitude || currentCoord.lat);
          const currentLng = parseFloat(currentCoord.longitude || currentCoord.lng);
          const nextLat = parseFloat(nextCoord.latitude || nextCoord.lat);
          const nextLng = parseFloat(nextCoord.longitude || nextCoord.lng);

          if (!isNaN(currentLat) && !isNaN(currentLng) && !isNaN(nextLat) && !isNaN(nextLng)) {
            const segmentDistance = calculateHaversineDistance(currentLat, currentLng, nextLat, nextLng);
            cumulativeDistance += segmentDistance;
          }
        }
      }
    } else {
      // Calculate step-by-step TO SOURCE (backward from stop to start)
      // S3 → S2 → S1 (if S3 is at index 2, source is at index 0)
      for (let i = stopIndex - 1; i >= 0; i--) {
        const currentCoord = routeCoordinates[i + 1]; // Start from stop position
        const prevCoord = routeCoordinates[i];        // Go backwards

        if (currentCoord && prevCoord) {
          const currentLat = parseFloat(currentCoord.latitude || currentCoord.lat);
          const currentLng = parseFloat(currentCoord.longitude || currentCoord.lng);
          const prevLat = parseFloat(prevCoord.latitude || prevCoord.lat);
          const prevLng = parseFloat(prevCoord.longitude || prevCoord.lng);

          if (!isNaN(currentLat) && !isNaN(currentLng) && !isNaN(prevLat) && !isNaN(prevLng)) {
            const segmentDistance = calculateHaversineDistance(currentLat, currentLng, prevLat, prevLng);
            cumulativeDistance += segmentDistance;
          }
        }
      }
    }

    console.log(`Step-by-step distance from stop ${stop.id} to ${isToDestination ? 'destination' : 'source'}: ${cumulativeDistance.toFixed(3)}km`);

    return cumulativeDistance;
  };

  useEffect(() => {
    if (!stopsData?.length || !routeData?.length) {
      console.log("[StopDistances] No stops or routes available, skipping distance calculation");
      return;
    }

    const calculateStopDistances = () => {
      const distances = {};

      // Process stops
      const stopsToProcess = stopsData.filter(stop => hasValidCoordinates(stop));

      stopsToProcess.forEach((stop) => {
        // Find routes that contain this stop
        const relevantRoutes = routeData.filter(route => {
          if (!route.routeCoordinates?.length) return false;
          const nearestIndex = findNearestRouteCoordinateIndex(stop, route.routeCoordinates);
          return nearestIndex !== -1;
        });

        if (relevantRoutes.length === 0) {
          console.warn(`[StopDistances] No relevant routes found for stop ${stop.id}`);
          return;
        }

        const route = relevantRoutes[0];

        // Calculate step-by-step distances (no need for targetPoint anymore)
        const distanceToSource = calculateRouteBasedDistance(stop, null, route, false);
        const distanceToDestination = calculateRouteBasedDistance(stop, null, route, true);

        // Get source and destination for direct distance comparison
        const sortedCoords = route.routeCoordinates.sort((a, b) => (a.order || 0) - (b.order || 0));
        const sourcePoint = sortedCoords[0];
        const destinationPoint = sortedCoords[sortedCoords.length - 1];

        const directDistanceToSource = calculateHaversineDistance(
          parseFloat(stop.latitude || stop.lat),
          parseFloat(stop.longitude || stop.lng),
          parseFloat(sourcePoint.latitude || sourcePoint.lat),
          parseFloat(sourcePoint.longitude || sourcePoint.lng)
        );

        const directDistanceToDestination = calculateHaversineDistance(
          parseFloat(stop.latitude || stop.lat),
          parseFloat(stop.longitude || stop.lng),
          parseFloat(destinationPoint.latitude || destinationPoint.lat),
          parseFloat(destinationPoint.longitude || destinationPoint.lng)
        );

        if (distanceToSource !== null && distanceToDestination !== null) {
          distances[stop.id] = {
            // Step-by-step route distances (CORRECT METHOD)
            routeDistanceToSource: distanceToSource.toFixed(3),
            routeDistanceToDestination: distanceToDestination.toFixed(3),

            // Direct distances for comparison
            directDistanceToSource: directDistanceToSource.toFixed(3),
            directDistanceToDestination: directDistanceToDestination.toFixed(3),

            routeId: route.id,
            stopName: stop.name || `Stop ${stop.id}`
          };

          console.log(
            `[StopDistances] Stop ${stop.id} (${stop.name || 'Unnamed'}):`,
            `Step-by-step to source: ${distanceToSource.toFixed(3)}km,`,
            `Step-by-step to destination: ${distanceToDestination.toFixed(3)}km,`,
            `Direct to source: ${directDistanceToSource.toFixed(3)}km,`,
            `Direct to destination: ${directDistanceToDestination.toFixed(3)}km`
          );
        } else {
          console.warn(`[StopDistances] Could not calculate distances for stop ${stop.id}`);
        }
      });

      setStopDistances(distances);
      console.log("[StopDistances] Final computed distances:", distances);
    };

    calculateStopDistances();
  }, [stopsData, routeData]);

  useEffect(() => {
    if (!stopsData?.length || !routeData?.length) {
      console.log("[StopToStopDistances] No stops or routes available, skipping calculation");
      return;
    }

    const calculateStopToStopDistances = () => {
      const distances = {};
      const route = routeData[0];
      if (!route?.routeCoordinates?.length) {
        console.warn("[StopToStopDistances] No route coordinates available");
        return;
      }

      const routeCoordinates = route.routeCoordinates.sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );

      const sourcePoint = routeCoordinates[0];
      const destinationPoint = routeCoordinates[routeCoordinates.length - 1];
      const selectedStops = stopsData.filter(stop => hasValidCoordinates(stop));

      const allPoints = [
        {
          id: sourcePoint.id || "source",
          latitude: sourcePoint.latitude || sourcePoint.lat,
          longitude: sourcePoint.longitude || sourcePoint.lng,
        },
        ...selectedStops.map(stop => ({
          id: stop.id,
          latitude: parseFloat(stop.latitude),
          longitude: parseFloat(stop.longitude),
        })),
        {
          id: destinationPoint.id || "destination",
          latitude: destinationPoint.latitude || destinationPoint.lat,
          longitude: destinationPoint.longitude || destinationPoint.lng,
        },
      ];

      allPoints.forEach((point1) => {
        const parentKey = `${route.id}_${point1.id}`;
        distances[parentKey] = {};

        allPoints.forEach((point2) => {
          if (point1.id === point2.id) return;

          const point1Index = findNearestRouteCoordinateIndex(point1, routeCoordinates);
          const point2Index = findNearestRouteCoordinateIndex(point2, routeCoordinates);

          if (point1Index === -1 || point2Index === -1) {
            console.warn(`[StopToStopDistances] Could not find route indices for ${point1.id}_${point2.id}`);
            return;
          }

          const distance = calculateRouteSegmentDistance(routeCoordinates, point1Index, point2Index);
          if (distance !== null) {
            distances[parentKey][point2.id] = parseFloat(distance.toFixed(3));
          }
        });
      });

      setStopToStopDistances(distances);
      console.log("[StopToStopDistances] Final grouped stop-to-stop distances:", distances);
    };

    calculateStopToStopDistances();
  }, [stopsData, routeData]);

  highlightedStopIdRef.current = highlightedStopId;

  const handleArrivalCandidate = useCallback((panelKey, candidate) => {
    const bucket = arrivalCandidatesRef.current;
    if (!candidate) {
      if (!bucket[panelKey]) return;
      delete bucket[panelKey];
    } else {
      bucket[panelKey] = candidate;
    }

    const best = Object.values(bucket)
      .filter(Boolean)
      .sort((left, right) => left.etaSeconds - right.etaSeconds)[0] || null;
    const next = best
      ? buildStopAnnouncement({
          vehicleId: best.vehicleId,
          vehicleName: best.vehicleName,
          destination: best.destination,
          etaSeconds: best.etaSeconds,
          stopId: best.stopId ?? highlightedStopIdRef.current,
          tripStatus: best.tripStatus,
        })
      : null;

    setStopAnnouncement((current) => {
      if ((current?.dedupeKey || null) === (next?.dedupeKey || null)) return current;
      return next;
    });
  }, []);

  const [leadVehicle, setLeadVehicle] = useState(null);
  const handleLeadVehicle = useCallback((next) => {
    setLeadVehicle(next);
  }, []);
  const publicAd = usePublicScreenAd({
    organizationId: orgId,
    screen: "stop",
  });
  const tickerVisible = publicAd.tickerAds.length > 0 && !publicAd.isPlaying;

  const { soundBlocked, enableAnnouncementSound } = useStopArrivalAnnouncement(
    stopAnnouncement,
    settings,
    publicAd.blockAnnouncements,
  );

  if (loadError) return <div>Error loading Google Maps</div>;
  if (isLoading || !isLoaded) return <LoadingScreen />;

  return (
    <Container maxWidth="false" sx={{ p: { xs: 0.5, sm: 1 }, ...adTickerSpaceSx(tickerVisible) }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {highlightedStopName && (
        <Typography variant="h3" sx={{ fontSize: 24, fontWeight: 700, textAlign: "center", mb: 0.5 }}>
          {highlightedStopName}
        </Typography>
      )}
      {soundBlocked ? (
        <Box
          onClick={enableAnnouncementSound}
          sx={{
            mb: 1,
            backgroundColor: "#F9E719",
            color: "#183059",
            padding: "10px 16px",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 700,
            textAlign: "center",
            cursor: "pointer",
          }}
        >
          Tap to enable announcements
        </Box>
      ) : null}
      {stopAnnouncement?.schedule?.text ? (
        <Box
          sx={{
            mb: 1,
            backgroundColor: stopAnnouncement.schedule.phase === "stop_delay" ? "#7A1F1F" : "#1F4E79",
            color: "#F9E719",
            padding: "8px 16px",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 600,
            textAlign: "center",
            fontSize: { xs: "0.95rem", sm: "1.15rem" },
          }}
        >
          {stopAnnouncement.schedule.text}
        </Box>
      ) : null}
      {InfoSide === "left" && (
        <Grid
          container
          spacing={{ xs: 1, sm: 2 }}
          sx={{
            height: { xs: "auto", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Grid
            item
            xs={11}
            md={4}
            sx={{
              height: { xs: "auto", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
              mb: { xs: 2, md: 0 },
            }}
          >
            <VehicleInfoPanel
              vehicles={vehiclesToSource}
              calculating={calculatingDistances}
              isLeftPanel={true}
              InfoSide={InfoSide}
              vehicleStopStatuses={vehicleStopStatuses}
              routeCoordinates={routeData.flatMap((r) => r.routeCoordinates || [])}
              stopsData={stopsData}
              highlightedStopId={highlightedStopId}
              vehicleDirections={vehicleDirections}
              stopDistances={stopDistances}
              stopToStopDistances={stopToStopDistances} // New prop
              panelKey="to-source"
              onLeadVehicle={handleLeadVehicle}
              onArrivalCandidate={handleArrivalCandidate}
            />
          </Grid>
          <Grid
            item
            xs={10}
            md={8}
            sx={{
              height: { xs: "300px", sm: "400px", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
              mb: { xs: 2, md: 0 },
            }}
          >
            <MapComponent
              mapCenter={mapCenter}
              onMapLoad={(map) => (mapRef.current = map)}
              directions={directions}
              filteredStops={filteredStops}
              highlightedStopId={highlightedStopId}
              onStopClick={(stop) => setHighlightedStopId(stop.id)}
              vehicleData={vehicleData}
              labelPositions={{}}
              allVehicles={visibleVehicles}
              routesForStop={routesForStop}
              vehicleDirections={vehicleDirections}
              allRoutes={routeData}
            />
          </Grid>
        </Grid>
      )}
      {InfoSide === "right" && (
        <Grid
          container
          spacing={{ xs: 1, sm: 2 }}
          sx={{
            height: { xs: "auto", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              height: { xs: "auto", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
            }}
          >
            <VehicleInfoPanel
              vehicles={vehiclesToSource}
              calculating={calculatingDistances}
              isLeftPanel={true}
              InfoSide={InfoSide}
              vehicleStopStatuses={vehicleStopStatuses}
              routeCoordinates={routeData.flatMap((r) => r.routeCoordinates || [])}
              stopsData={stopsData}
              highlightedStopId={highlightedStopId}
              vehicleDirections={vehicleDirections}
              stopDistances={stopDistances}
              stopToStopDistances={stopToStopDistances} // New prop
              panelKey="to-source"
              onLeadVehicle={handleLeadVehicle}
              onArrivalCandidate={handleArrivalCandidate}
            />
            <Box
              sx={{
                fontFamily: "Roboto",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "16px",
                lineHeight: "100%",
                color: "#000000",
                padding: "16px",
                alignItems: "center",
                margin: "0px auto",
                display: { xs: "none", md: "flex" },
                gap: "10px",
              }}
            >
              Powered by
              <img src={PoweredBy_Logo} alt="Powered by logo" />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              height: { xs: "240px", sm: "400px", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
              mb: { xs: 0, sm: 2, md: 0 },
              position: "relative",
            }}
          >
            <MapComponent
              mobileHeight="240px"
              mapCenter={mapCenter}
              onMapLoad={(map) => (mapRef.current = map)}
              directions={directions}
              filteredStops={filteredStops}
              highlightedStopId={highlightedStopId}
              onStopClick={(stop) => setHighlightedStopId(stop.id)}
              vehicleData={vehicleData}
              labelPositions={{}}
              allVehicles={visibleVehicles}
              routesForStop={routesForStop}
              vehicleDirections={vehicleDirections}
              allRoutes={routeData}
            />
            {showStopQRCode && (
              <StopQrCode stopId={selectedStop?.id} organizationId={orgId} stopName={highlightedStopName} />
             )} 
          </Grid>
          <Grid item xs={12} sx={{ display: { xs: "block", md: "none" } }}>
            <Box
              sx={{
                fontFamily: "Roboto",
                fontWeight: 400,
                fontStyle: "normal",
                fontSize: "16px",
                lineHeight: "100%",
                color: "#000000",
                padding: "16px",
                alignItems: "center",
                margin: "0px auto",
                display: "flex",
                gap: "10px",
              }}
            >
              Powered by
              <img src={PoweredBy_Logo} alt="Powered by logo" />
            </Box>
          </Grid>
        </Grid>
      )}
      {!InfoSide && (
        <Grid
          container
          spacing={{ xs: 1, sm: 2 }}
          sx={{
            height: { xs: "auto", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              height: { xs: "auto", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {selectedStop && routesForStop.length > 0 && (
                <>
                  <VehicleInfoPanel
                    vehicles={vehiclesToSource}
                    calculating={calculatingDistances}
                    isLeftPanel={true}
                    InfoSide={InfoSide}
                    hideHeader={false}
                    vehicleStopStatuses={vehicleStopStatuses}
                    routeCoordinates={routeData.flatMap((r) => r.routeCoordinates || [])} // Already present
                    stopsData={stopsData}
                    highlightedStopId={highlightedStopId}
                    vehicleDirections={vehicleDirections}
                    stopDistances={stopDistances}
                    stopToStopDistances={stopToStopDistances} // New prop
                    panelKey="to-source"
              onLeadVehicle={handleLeadVehicle}
                    onArrivalCandidate={handleArrivalCandidate}
                    headerText={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={destinationSVG}
                            alt="source"
                            style={{ width: 25, height: 25 }}
                          />
                        </Box>
                        <Typography variant="body1" fontWeight={600}>
                          Towards {getSourceStopName(routesForStop[0])}
                        </Typography>
                      </Box>
                    }
                  />
                  <VehicleInfoPanel
                    vehicles={vehiclesToDestination}
                    calculating={calculatingDistances}
                    isLeftPanel={false}
                    InfoSide={InfoSide}
                    hideHeader={false}
                    vehicleStopStatuses={vehicleStopStatuses}
                    routeCoordinates={routeData.flatMap((r) => r.routeCoordinates || [])} // Already present
                    stopsData={stopsData}
                    highlightedStopId={highlightedStopId}
                    vehicleDirections={vehicleDirections}
                    stopDistances={stopDistances}
                    stopToStopDistances={stopToStopDistances} // New prop
                    panelKey="to-destination"
                    onArrivalCandidate={handleArrivalCandidate}
                    headerText={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={destinationSVG}
                            alt="destination"
                            style={{ width: 25, height: 25 }}
                          />
                        </Box>
                        <Typography variant="body1" fontWeight={600}>
                          Towards {getDestinationStop(routesForStop[0], "source_to_dest", stopsData)}
                        </Typography>
                      </Box>
                    }
                  />

                </>
              )}
              <Box
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 300,
                  fontStyle: "normal",
                  fontSize: "14px",
                  lineHeight: "100%",
                  color: "#000000",
                  padding: "16px",
                  alignItems: "center",
                  margin: "0px auto",
                  display: "flex",
                  gap: "10px",
                }}
              >
                Powered by
                <img src={PoweredBy_Logo} alt="Powered by logo" />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              height: { xs: "300px", sm: "400px", md: "calc(97vh - var(--ad-ticker-space, 0px))" },
              mb: { xs: 2, md: 0 },
            }}
          >
            <MapComponent
              mapCenter={mapCenter}
              onMapLoad={(map) => (mapRef.current = map)}
              directions={directions}
              filteredStops={filteredStops}
              highlightedStopId={highlightedStopId}
              onStopClick={(stop) => setHighlightedStopId(stop.id)}
              vehicleData={vehicleData}
              labelPositions={{}}
              allVehicles={visibleVehicles}
              routesForStop={routesForStop}
              vehicleDirections={vehicleDirections}
              allRoutes={routeData}
              vehicleStatuses={vehicleStatuses}
            />
          </Grid>
        </Grid>
      )}
      <PublicAdOverlay
        open={publicAd.isPlaying}
        ad={publicAd.currentAd}
        tickerPosition={publicAd.tickerPosition}
        items={[
          { label: "Next vehicle", value: leadVehicle?.name },
          { label: "At", value: leadVehicle?.atStop },
          { label: "Towards", value: leadVehicle?.towards },
          { label: "Distance", value: leadVehicle?.distanceText },
          { label: "ETA", value: leadVehicle?.etaText },
        ]}
      />
      <PublicAdTicker ads={publicAd.tickerAds} hidden={publicAd.isPlaying} />
    </Container>
  );
}
